// Canvas helpers for the Art Room
export const mkCanvas = (w, h) => { const c = document.createElement("canvas"); c.width = w; c.height = h; return c; };
export const cloneCanvas = (src) => { const c = mkCanvas(src.width, src.height); c.getContext("2d").drawImage(src, 0, 0); return c; };
export const lid = () => "L" + Math.random().toString(36).slice(2, 8);

export const hexToRgb = (hex) => { const h = hex.replace("#", ""); const n = parseInt(h.length === 3 ? h.split("").map((x) => x + x).join("") : h, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
export const rgbToHex = (r, g, b) => "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");

export const BLENDS = ["source-over", "multiply", "screen", "overlay", "darken", "lighten", "color-dodge", "color-burn", "hard-light", "soft-light", "difference", "exclusion", "hue", "saturation", "color", "luminosity"];
export const blendLabel = (b) => (b === "source-over" ? "Normal" : b.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join(" "));

export function stamp(ctx, x, y, size, hardness, color, erase) {
  const r = size / 2;
  const [cr, cg, cb] = hexToRgb(color);
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  const h = Math.min(0.99, Math.max(0, hardness));
  g.addColorStop(0, `rgba(${cr},${cg},${cb},1)`);
  g.addColorStop(h, `rgba(${cr},${cg},${cb},1)`);
  g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
  ctx.globalCompositeOperation = erase ? "destination-out" : "source-over";
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  ctx.globalCompositeOperation = "source-over";
}

export function strokeLine(ctx, a, b, size, hardness, color, erase) {
  const d = Math.hypot(b.x - a.x, b.y - a.y), step = Math.max(1, size * 0.12);
  for (let i = 0; i <= d; i += step) { const t = d ? i / d : 0; stamp(ctx, a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, size, hardness, color, erase); }
}

export function floodFill(canvas, x, y, color, tolerance, alpha = 1, sel) {
  const ctx = canvas.getContext("2d"), w = canvas.width, h = canvas.height;
  x = Math.floor(x); y = Math.floor(y);
  if (x < 0 || y < 0 || x >= w || y >= h) return;
  const img = ctx.getImageData(0, 0, w, h), d = img.data;
  const i0 = (y * w + x) * 4, t = [d[i0], d[i0 + 1], d[i0 + 2], d[i0 + 3]];
  const [r, g, b] = hexToRgb(color), a = Math.round(alpha * 255);
  const tol = tolerance * 4;
  const match = (i) => Math.abs(d[i] - t[0]) + Math.abs(d[i + 1] - t[1]) + Math.abs(d[i + 2] - t[2]) + Math.abs(d[i + 3] - t[3]) <= tol;
  const seen = new Uint8Array(w * h);
  const stack = [[x, y]];
  const inSel = (px, py) => !sel || (px >= sel.x && px < sel.x + sel.w && py >= sel.y && py < sel.y + sel.h);
  while (stack.length) {
    const [px, py] = stack.pop();
    let lx = px; while (lx >= 0 && !seen[py * w + lx] && match((py * w + lx) * 4) && inSel(lx, py)) lx--;
    lx++;
    let up = false, dn = false;
    for (let cx = lx; cx < w; cx++) {
      const p = py * w + cx, i = p * 4;
      if (seen[p] || !match(i) || !inSel(cx, py)) break;
      seen[p] = 1; d[i] = r; d[i + 1] = g; d[i + 2] = b; d[i + 3] = a;
      if (py > 0) { const q = (py - 1) * w + cx; if (!seen[q] && match(q * 4)) { if (!up) { stack.push([cx, py - 1]); up = true; } } else up = false; }
      if (py < h - 1) { const q = (py + 1) * w + cx; if (!seen[q] && match(q * 4)) { if (!dn) { stack.push([cx, py + 1]); dn = true; } } else dn = false; }
    }
  }
  ctx.putImageData(img, 0, 0);
}

export function applyCssFilter(canvas, filter) {
  const c = mkCanvas(canvas.width, canvas.height), ctx = c.getContext("2d");
  ctx.filter = filter; ctx.drawImage(canvas, 0, 0);
  return c;
}

export function pixelOp(canvas, op) {
  const c = cloneCanvas(canvas), ctx = c.getContext("2d");
  const img = ctx.getImageData(0, 0, c.width, c.height), d = img.data;
  if (op === "noise") for (let i = 0; i < d.length; i += 4) { const n = (Math.random() - 0.5) * 50; d[i] += n; d[i + 1] += n; d[i + 2] += n; }
  if (op === "posterize") { const lv = 5, s = 255 / (lv - 1); for (let i = 0; i < d.length; i += 4) for (let k = 0; k < 3; k++) d[i + k] = Math.round(Math.round(d[i + k] / s) * s); }
  if (op === "threshold") for (let i = 0; i < d.length; i += 4) { const v = 0.3 * d[i] + 0.59 * d[i + 1] + 0.11 * d[i + 2] > 128 ? 255 : 0; d[i] = d[i + 1] = d[i + 2] = v; }
  if (op === "sharpen") {
    const w = c.width, h = c.height, src = new Uint8ClampedArray(d), k = [0, -1, 0, -1, 5, -1, 0, -1, 0];
    for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) for (let ch = 0; ch < 3; ch++) {
      let s = 0, ki = 0; for (let ky = -1; ky <= 1; ky++) for (let kx = -1; kx <= 1; kx++) s += src[((y + ky) * w + (x + kx)) * 4 + ch] * k[ki++];
      d[(y * w + x) * 4 + ch] = s;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

export function flip(canvas, dir) {
  const c = mkCanvas(canvas.width, canvas.height), ctx = c.getContext("2d");
  ctx.translate(dir === "h" ? c.width : 0, dir === "v" ? c.height : 0); ctx.scale(dir === "h" ? -1 : 1, dir === "v" ? -1 : 1); ctx.drawImage(canvas, 0, 0);
  return c;
}

export function demoLayers(w, h) {
  const bg = mkCanvas(w, h), b = bg.getContext("2d");
  const g = b.createLinearGradient(0, 0, 0, h); g.addColorStop(0, "#1a1206"); g.addColorStop(1, "#050506");
  b.fillStyle = g; b.fillRect(0, 0, w, h);
  const glow = mkCanvas(w, h), gl = glow.getContext("2d");
  const rg = gl.createRadialGradient(w / 2, h * 0.42, 10, w / 2, h * 0.42, w * 0.45); rg.addColorStop(0, "rgba(251,191,36,.55)"); rg.addColorStop(1, "rgba(251,191,36,0)");
  gl.fillStyle = rg; gl.fillRect(0, 0, w, h);
  const ring = mkCanvas(w, h), r = ring.getContext("2d");
  r.strokeStyle = "#fbbf24"; r.lineWidth = 6; r.beginPath(); r.arc(w / 2, h * 0.42, w * 0.22, 0, Math.PI * 2); r.stroke();
  r.lineWidth = 2; r.strokeStyle = "rgba(251,191,36,.5)"; r.beginPath(); r.arc(w / 2, h * 0.42, w * 0.26, 0, Math.PI * 2); r.stroke();
  const txt = mkCanvas(w, h), t = txt.getContext("2d");
  t.fillStyle = "#ffffff"; t.textAlign = "center"; t.font = `300 ${Math.round(w * 0.085)}px 'IBM Plex Sans', sans-serif`;
  t.fillText("IN THE POCKET", w / 2, h * 0.8);
  t.fillStyle = "#fbbf24"; t.font = `500 ${Math.round(w * 0.028)}px 'IBM Plex Mono', monospace`;
  t.fillText("LIVE  ·  FRIDAY 10.09  ·  THE BLUE ROOM", w / 2, h * 0.87);
  const L = (name, canvas, extra = {}) => ({ id: lid(), name, canvas, visible: true, opacity: 1, blend: "source-over", x: 0, y: 0, ...extra });
  return [L("Background", bg), L("Glow", glow, { blend: "screen" }), L("Ring", ring), L("Title", txt)];
}
